import type { Action, Entity, EntityState } from './collections.types';
import { ITEM_INSERTED, ITEM_REMOVED } from './action-types';

// Type for actions that affect child entities
interface ChildAction extends Action {
  meta: {
    type: string;
    parent?: string;
  };
  payload: {
    id: string;
    index?: number;
    [key: string]: any;
  };
}

// Type for child item with position info
interface ChildItem {
  id: string;
  index?: number;
}

/**
 * Get child item from action payload, handling different payload shapes
 */
const getChild = (action: ChildAction): ChildItem => {
  const { payload, meta } = action;

  // Simple case - payload has id directly
  if (payload.id) {
    return payload;
  }

  // Resolve-siblings case - payload is nested by entity type
  if (meta && payload[meta.type]) {
    const values = Object.values(payload[meta.type]);
    const [item] = values;

    if (
      !item ||
      typeof item !== 'object' ||
      !('id' in item) ||
      typeof item.id !== 'string'
    ) {
      throw new Error('Invalid child item structure');
    }

    return item as ChildItem;
  }

  throw new Error('Cannot extract child from action payload');
};

/**
 * Insert item into array at specified index
 */
const insert = (basket: string[], { id, index }: ChildItem): string[] => {
  const insertIndex = index !== undefined ? index : basket.length;
  return [...basket.slice(0, insertIndex), id, ...basket.slice(insertIndex)];
};

/**
 * Remove item from array by ID
 */
const remove = (basket: string[], { id }: { id: string }): string[] =>
  basket.filter(item => item !== id);

/**
 * Reducer enhancer for managing parent-child relationships in collections.
 * Handles insertion and removal of child entities in parent entity arrays.
 *
 * @param field - The field name in parent entities that holds child IDs
 * @param childType - The entity type of child items to manage
 * @returns Enhanced reducer function
 */
export default function hasMany<T extends Entity>(
  field: string,
  childType: string
): (state: EntityState<T>, action: Action) => EntityState<T> {
  /**
   * Type guard to check if action is a child insertion
   */
  const shouldInsert = (action: Action): action is ChildAction =>
    !!(
      action.meta &&
      action.meta.type === childType &&
      action.meta.parent &&
      action.type === ITEM_INSERTED
    );

  /**
   * Type guard to check if action is a child removal
   */
  const shouldRemove = (action: Action): action is ChildAction =>
    !!(
      action.meta &&
      action.meta.type === childType &&
      action.meta.parent &&
      action.type === ITEM_REMOVED
    );

  return (state: EntityState<T>, action: Action): EntityState<T> => {
    if (shouldInsert(action)) {
      const parentId = action.meta.parent!;
      const parent = state[parentId];
      const child = getChild(action);

      if (parent) {
        // Update existing parent entity
        const currentChildren = (parent as any)[field] || [];
        return {
          ...state,
          [parentId]: {
            ...parent,
            [field]: insert(currentChildren, child),
          } as T,
        };
      }

      // Update root state field if no specific parent
      const currentChildren = (state as any)[field] || [];
      return {
        ...state,
        [field]: insert(currentChildren, child),
      } as EntityState<T>;
    }

    if (shouldRemove(action)) {
      const parentId = action.meta.parent!;
      const parent = state[parentId];
      const childId = action.payload.id;

      if (parent) {
        // Update existing parent entity
        const currentChildren = (parent as any)[field] || [];
        return {
          ...state,
          [parentId]: {
            ...parent,
            [field]: remove(currentChildren, { id: childId }),
          } as T,
        };
      }

      // Update root state field if no specific parent
      const currentChildren = (state as any)[field] || [];
      return {
        ...state,
        [field]: remove(currentChildren, { id: childId }),
      } as EntityState<T>;
    }

    return state;
  };
}
